type Rgba = {
    r: number;
    g: number;
    b: number;
    a: number;
};

type RgbTuple = [number, number, number];

const WHITE_RGB: RgbTuple = [255, 255, 255];
const BLACK_RGB: RgbTuple = [0, 0, 0];
const WHITE_TEXT = '#ffffff';
const BLACK_TEXT = '#000000';

function clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
}

function parseHexToRgba(value: string): Rgba | null {
    const hex = value.trim().replace('#', '');

    if (hex.length === 3) {
        return {
            r: parseInt(`${hex[0]}${hex[0]}`, 16),
            g: parseInt(`${hex[1]}${hex[1]}`, 16),
            b: parseInt(`${hex[2]}${hex[2]}`, 16),
            a: 1,
        };
    }

    if (hex.length === 4) {
        return {
            r: parseInt(`${hex[0]}${hex[0]}`, 16),
            g: parseInt(`${hex[1]}${hex[1]}`, 16),
            b: parseInt(`${hex[2]}${hex[2]}`, 16),
            a: parseInt(`${hex[3]}${hex[3]}`, 16) / 255,
        };
    }

    if (hex.length === 6) {
        return {
            r: parseInt(hex.substring(0, 2), 16),
            g: parseInt(hex.substring(2, 4), 16),
            b: parseInt(hex.substring(4, 6), 16),
            a: 1,
        };
    }

    if (hex.length === 8) {
        return {
            r: parseInt(hex.substring(0, 2), 16),
            g: parseInt(hex.substring(2, 4), 16),
            b: parseInt(hex.substring(4, 6), 16),
            a: parseInt(hex.substring(6, 8), 16) / 255,
        };
    }

    return null;
}

function parseRgbFunctionToRgba(value: string): Rgba | null {
    const match = value
        .trim()
        .match(/^rgba?\(([^)]+)\)$/i);

    if (!match) {
        return null;
    }

    const parts = match[1]
        .split(',')
        .map((part) => part.trim());

    if (parts.length < 3 || parts.length > 4) {
        return null;
    }

    const r = Number(parts[0]);
    const g = Number(parts[1]);
    const b = Number(parts[2]);
    const a = parts.length === 4 ? Number(parts[3]) : 1;

    if ([r, g, b, a].some((component) => Number.isNaN(component))) {
        return null;
    }

    return {
        r: clamp(r, 0, 255),
        g: clamp(g, 0, 255),
        b: clamp(b, 0, 255),
        a: clamp(a, 0, 1),
    };
}

function parseColorToOpaqueRgb(color: string): RgbTuple | null {
    if (!color) {
        return null;
    }

    const parsed = color.trim().startsWith('#')
        ? parseHexToRgba(color)
        : parseRgbFunctionToRgba(color);

    if (!parsed) {
        return null;
    }

    const alpha = clamp(parsed.a, 0, 1);

    const toOpaque = (channel: number): number => {
        const blended = channel * alpha + 255 * (1 - alpha);
        return Math.round(clamp(blended, 0, 255));
    };

    return [toOpaque(parsed.r), toOpaque(parsed.g), toOpaque(parsed.b)];
}

function rgbToHex([r, g, b]: RgbTuple): string {
    const channel = (value: number) => clamp(Math.round(value), 0, 255)
        .toString(16)
        .padStart(2, '0');

    return `#${channel(r)}${channel(g)}${channel(b)}`;
}

function blendRgb(from: RgbTuple, to: RgbTuple, ratio: number): RgbTuple {
    const t = clamp(ratio, 0, 1);
    return [
        Math.round(from[0] + (to[0] - from[0]) * t),
        Math.round(from[1] + (to[1] - from[1]) * t),
        Math.round(from[2] + (to[2] - from[2]) * t),
    ];
}

function relativeLuminance([r, g, b]: RgbTuple): number {
    const normalize = (c: number): number => {
        const v = c / 255;
        return v <= 0.03928
            ? v / 12.92
            : ((v + 0.055) / 1.055) ** 2.4;
    };

    return (
        0.2126 * normalize(r) +
        0.7152 * normalize(g) +
        0.0722 * normalize(b)
    );
}

function contrastRatio(l1: number, l2: number): number {
    const [light, dark] = l1 > l2 ? [l1, l2] : [l2, l1];
    return (light + 0.05) / (dark + 0.05);
}

function getContrastForText(background: RgbTuple, text: typeof WHITE_TEXT | typeof BLACK_TEXT): number {
    const bgLuminance = relativeLuminance(background);
    const textLuminance = text === WHITE_TEXT ? 1 : 0;
    return contrastRatio(textLuminance, bgLuminance);
}

function ensureMinContrast(
    background: RgbTuple,
    text: typeof WHITE_TEXT | typeof BLACK_TEXT,
    minContrast: number,
): RgbTuple {
    const currentContrast = getContrastForText(background, text);
    if (currentContrast >= minContrast) {
        return background;
    }

    const target = text === WHITE_TEXT ? BLACK_RGB : WHITE_RGB;

    let low = 0;
    let high = 1;
    let best = background;

    for (let i = 0; i < 20; i += 1) {
        const mid = (low + high) / 2;
        const candidate = blendRgb(background, target, mid);
        const candidateContrast = getContrastForText(candidate, text);

        if (candidateContrast >= minContrast) {
            best = candidate;
            high = mid;
        } else {
            low = mid;
        }
    }

    return best;
}

export function getAccessibleTagColors(
    background: string,
    minContrast = 4.5,
): { backgroundColor: string; textColor: typeof WHITE_TEXT | typeof BLACK_TEXT } {
    const parsedBackground = parseColorToOpaqueRgb(background) ?? WHITE_RGB;

    const whiteContrast = getContrastForText(parsedBackground, WHITE_TEXT);
    const blackContrast = getContrastForText(parsedBackground, BLACK_TEXT);

    const preferredText = whiteContrast >= blackContrast ? WHITE_TEXT : BLACK_TEXT;
    const adjustedBackground = ensureMinContrast(parsedBackground, preferredText, minContrast);

    return {
        backgroundColor: rgbToHex(adjustedBackground),
        textColor: preferredText,
    };
}

export function getAccessibleTextColor(
    background: string,
    minContrast = 4.5,
): '#000000' | '#ffffff' {
    return getAccessibleTagColors(background, minContrast).textColor;
}
